import { useState, useEffect } from 'react';
import { BizTab } from '../types';

interface VersionRecommenderProps {
  onOpenDrawer: (tab: BizTab, defaultNote?: string, defaultNeedTax?: boolean) => void;
}

type EntityCount = '1' | '2' | '3-5' | '5+';
type RevenueRange = '≤300' | '300-500' | '500-1000' | '1000+';

interface Recommendation {
  version: string;
  price: string;
  reasons: string[];
  needTax?: boolean;
}

export default function VersionRecommender({ onOpenDrawer }: VersionRecommenderProps) {
  const [bizType, setBizType] = useState<BizTab>('9810');
  const [entityCount, setEntityCount] = useState<EntityCount>('1');
  const [revenue, setRevenue] = useState<RevenueRange>('≤300');
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);

  // 计算推荐版本
  useEffect(() => {
    const result = calculateRecommendation(bizType, entityCount, revenue);
    setRecommendation(result);
  }, [bizType, entityCount, revenue]);

  const handleConsult = () => {
    const note = `业务类型：${getBizTypeLabel(bizType)}，主体数：${entityCount}，年度规模：${getRevenueLabel(bizType, revenue)}`;
    onOpenDrawer(bizType, note, recommendation?.needTax);
  };

  return (
    <div className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        {/* 标题 */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">如何选择版本？</h2>
          <p className="text-gray-500 text-lg">回答3个问题，获取专属推荐方案</p>
        </div>

        {/* 问题区 */}
        <div className="bg-white rounded-3xl shadow-xl p-10 mb-8">
          <div className="space-y-10">
            {/* 问题1：业务类型 */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-4">
                1. 您的业务类型是？
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(['9810', '0110', '1039', 'A'] as BizTab[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setBizType(type)}
                    className={`h-16 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap ${
                      bizType === type
                        ? 'bg-gradient-to-r from-[#14B8A6] to-[#0D9488] text-white shadow-lg scale-105'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {getBizTypeLabel(type)}
                  </button>
                ))}
              </div>
            </div>

            {/* 问题2：主体数 */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-4">
                2. 您的主体数量是？
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(['1', '2', '3-5', '5+'] as EntityCount[]).map((count) => (
                  <button
                    key={count}
                    onClick={() => setEntityCount(count)}
                    className={`h-16 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap ${
                      entityCount === count
                        ? 'bg-gradient-to-r from-[#14B8A6] to-[#0D9488] text-white shadow-lg scale-105'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {count === '5+' ? '5个以上' : `${count}个主体`}
                  </button>
                ))}
              </div>
            </div>

            {/* 问题3：年度规模 */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-4">
                3. 您的{getRevenueLabel(bizType, '')}区间是？
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(['≤300', '300-500', '500-1000', '1000+'] as RevenueRange[]).map((range) => (
                  <button
                    key={range}
                    onClick={() => setRevenue(range)}
                    className={`h-16 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap ${
                      revenue === range
                        ? 'bg-gradient-to-r from-[#14B8A6] to-[#0D9488] text-white shadow-lg scale-105'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {formatRevenueRange(range)}万
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 推荐结果 */}
        {recommendation && (
          <div className="bg-gradient-to-br from-[#14B8A6] to-[#0D9488] rounded-3xl shadow-2xl p-10 text-white">
            <div className="flex items-start gap-6">
              {/* 图标 */}
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-line text-3xl text-white"></i>
              </div>

              {/* 内容 */}
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2">为您推荐</h3>
                <div className="flex items-baseline gap-3 mb-6">
                  <span className="text-4xl font-bold">{recommendation.version}</span>
                  <span className="text-xl opacity-90">{recommendation.price}</span>
                </div>

                {/* 推荐原因 */}
                <div className="space-y-3 mb-8">
                  {recommendation.reasons.map((reason, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-white/90">{reason}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={handleConsult}
                    className="h-12 px-8 bg-white text-[#14B8A6] font-semibold rounded-full hover:scale-105 transition-transform cursor-pointer whitespace-nowrap shadow-lg"
                  >
                    咨询报价
                  </button>
                  <button
                    onClick={() => {
                      const element = document.getElementById('biz-type-tabs');
                      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="h-12 px-8 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap border border-white/30"
                  >
                    查看详细配置
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// 辅助函数：获取业务类型标签
function getBizTypeLabel(type: BizTab): string {
  const labels: Record<BizTab, string> = {
    '9810': '9810 海外仓/平台',
    '0110': '0110 一般贸易',
    '1039': '1039 无票出口',
    'A': 'A类自动化工具',
  };
  return labels[type];
}

// 辅助函数：获取规模标签
function getRevenueLabel(type: BizTab, range: string): string {
  if (range === '') {
    const labels: Record<BizTab, string> = {
      '9810': '年平台销售额',
      '0110': '年报关金额',
      '1039': '年出口额',
      'A': '年度规模',
    };
    return labels[type];
  }
  return range;
}

// 辅助函数：格式化规模区间
function formatRevenueRange(range: RevenueRange): string {
  if (range === '1000+') return '1000以上';
  return range;
}

// 核心推荐逻辑
function calculateRecommendation(
  bizType: BizTab,
  entityCount: EntityCount,
  revenue: RevenueRange
): Recommendation {
  // A类工具单独处理
  if (bizType === 'A') {
    return {
      version: 'A类自动化工具',
      price: '9.9 / 99 起',
      reasons: [
        '适合低频需求或首次验证闭环流程',
        '按票计费，无需长期订阅，灵活可控',
      ],
    };
  }

  // 1039单独处理
  if (bizType === '1039') {
    const needAdvanced = revenue === '500-1000' || revenue === '1000+';
    return {
      version: '1039 年度套餐',
      price: '22,800/年起',
      reasons: [
        '年度管理模式，适合多批次无票出口场景',
        needAdvanced ? '建议咨询增强服务包（核算/值守）' : '基础套餐已覆盖核心合规需求',
      ],
      needTax: needAdvanced,
    };
  }

  // 9810 和 0110 的推荐逻辑
  const isMultiEntity = entityCount !== '1';
  const isHighRevenue = revenue === '500-1000' || revenue === '1000+';
  const isMediumRevenue = revenue === '300-500';

  // 规模很小 + 单主体 → 轻量版
  if (!isMultiEntity && revenue === '≤300') {
    return {
      version: '轻量版',
      price: bizType === '9810' ? '2,500/年起' : '4,000/年起',
      reasons: [
        '适合单主体、低频出货场景',
        '覆盖基础合规管理需求，性价比高',
      ],
    };
  }

  // 中等规模或多主体 → 基础版
  if (!isHighRevenue && (isMediumRevenue || entityCount === '2')) {
    return {
      version: '基础版',
      price: bizType === '9810' ? '4,000/年起' : '8,000/年起',
      reasons: [
        '适合≤2户主体、年度规模≤300万场景',
        '包含完整合规管理闭环，超出需升级',
      ],
    };
  }

  // 高规模或多主体 → 标准版
  if (isHighRevenue || entityCount === '3-5') {
    return {
      version: '标准版（最受欢迎）',
      price: bizType === '9810' ? '9,000/年起' : '12,000/年起',
      reasons: [
        '适合3-5户主体或年度规模500万以上',
        '可加购退税服务包，支持复杂合规场景',
      ],
      needTax: true,
    };
  }

  // 超大规模或超多主体 → 尊享版
  return {
    version: '尊享版',
    price: bizType === '9810' ? '12,000/年起' : '16,000/年起',
    reasons: [
      '适合5户以上主体或年度规模1000万以上',
      '含值守保障，可叠加退税/核算服务包',
    ],
    needTax: true,
  };
}